import React from 'react';
import { z } from 'zod';
import type { BlockDefinition } from '../../types';

/**
 * Kathreftis marketing v3 — a faithful React rebuild of the "Nocturne" design the
 * team liked (dark, Inter-set, purple-accented). Section layouts are ported
 * verbatim from the design handoff as inline styles; the small design-system
 * utility classes (.btn/.card/.tag/.input/.field) + tokens live in the linked
 * stylesheet /kathreftis-v3/theme.css. Text/lists/links are CMS-editable props
 * (defaults = the real Kathrefits copy from the handoff). Block types are
 * prefixed `k3` to stay distinct from v1 (k*) and v2 (kathreftis*). Self-chrome:
 * the header + footer blocks are part of the page, so it renders bare.
 */

const A = '/kathreftis-v3';

// Palette echoed from the design (also in theme.css tokens) for inline styles.
const INK = '#e9e9ed';
const ACCENT = '#9184d9';
const ACCENT_LT = '#b5abfc';
const ACCENT_LT2 = '#d2cefd';
const SURFACE = '#232532';
const CARD_BORDER = '#3f424d';
const muted = (a: number) => `rgba(233,233,237,${a})`;

// ─── Shared: stylesheet link (emitted once, in the header) ─────────────────────
export const K3Head = () => (
  <link rel="stylesheet" href={`${A}/theme.css?v=1`} />
);

// ─── 1. Header ─────────────────────────────────────────────────────────────────
const navItem = z.object({ label: z.string(), href: z.string().default('#') });
export const k3HeaderSchema = z.object({
  brand: z.string().default('Kathrefits AI'),
  brandSuffix: z.string().default('Pvt Ltd'),
  logoUrl: z.string().default('/kathreftis/images/kathreftis-logo.png'), // navy logo on a white badge (v3 is dark)
  nav: z.array(navItem).default([
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ]),
  ctaLabel: z.string().default('Talk to us'),
  ctaHref: z.string().default('/contact'),
});
function K3Header(p: z.infer<typeof k3HeaderSchema>) {
  return (
    <>
      <K3Head />
      <header style={{ position: 'sticky', top: 0, zIndex: 30, backdropFilter: 'blur(14px)', background: 'rgba(22,24,38,0.86)', borderBottom: '1px solid rgba(233,233,237,0.10)' }}>
        <div className="k3-section-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 32 }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: 'auto' }}>
            <img src={p.logoUrl} alt={p.brand} style={{ height: 32, width: 'auto', display: 'block', background: '#fff', padding: '7px 14px', borderRadius: 12 }} />
          </a>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {p.nav.map((it, i) => (
              <a key={i} href={it.href} style={{ fontSize: 14, padding: '8px 13px', borderRadius: 8, textDecoration: 'none', color: i === 0 ? INK : muted(0.6), background: i === 0 ? 'rgba(145,132,217,0.16)' : 'transparent' }}>{it.label}</a>
            ))}
          </nav>
          <a className="btn btn-primary" href={p.ctaHref} style={{ fontSize: 13 }}>{p.ctaLabel}</a>
        </div>
      </header>
    </>
  );
}

// ─── 2. Hero ─────────────────────────────────────────────────────────────────
export const k3HeroSchema = z.object({
  eyebrow: z.string().default('Identity for the age of AI agents'),
  headline: z.string().default('Identity that governs people, machines and agents alike.'),
  body: z.string().default('Kathrefits AI is an Indian deep-tech product company. We build the identity fabric that decides who — and what — gets access: employees, customers, privileged admins, and now the AI agents acting on their behalf.'),
  primaryLabel: z.string().default('Talk to us'),
  primaryHref: z.string().default('/contact'),
  secondaryLabel: z.string().default('See the four products'),
  secondaryHref: z.string().default('/products'),
});
function K3Hero(p: z.infer<typeof k3HeroSchema>) {
  return (
    <section style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(233,233,237,0.09)' }}>
      <div style={{ position: 'absolute', top: -280, left: -120, width: 760, height: 620, background: 'radial-gradient(closest-side, rgba(145,132,217,0.22), transparent)', pointerEvents: 'none' }} />
      <div className="k3-section-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '104px 32px 88px', position: 'relative' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: ACCENT_LT, marginBottom: 26 }}>
          <span style={{ width: 22, height: 1, background: ACCENT, display: 'inline-block' }} />
          {p.eyebrow}
        </div>
        <h1 className="k3-hero-h1" style={{ fontSize: 66, lineHeight: 1.03, letterSpacing: '-0.03em', fontWeight: 500, maxWidth: '15ch', margin: '0 0 28px' }}>{p.headline}</h1>
        <p style={{ fontSize: 19, lineHeight: 1.6, maxWidth: '62ch', color: muted(0.68), margin: '0 0 36px', textWrap: 'pretty' }}>{p.body}</p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a className="btn btn-primary" href={p.primaryHref}>{p.primaryLabel}</a>
          <a className="btn btn-ghost" href={p.secondaryHref}>{p.secondaryLabel}</a>
        </div>
      </div>
    </section>
  );
}

// ─── 3. Stats strip ────────────────────────────────────────────────────────────
const stat = z.object({ value: z.string(), label: z.string() });
export const k3StatsSchema = z.object({
  stats: z.array(stat).default([
    { value: '60%', label: 'Fewer security breaches after rollout, on customer-reported figures' },
    { value: '50M+', label: 'Authentications served every month across the platforms' },
    { value: '99.9%', label: 'Uptime SLA on our managed deployments' },
    { value: '4', label: 'Shipping products, all built and owned in-house' },
  ]),
});
function K3Stats(p: z.infer<typeof k3StatsSchema>) {
  return (
    <section style={{ background: 'linear-gradient(180deg, #262a60, #1f2350)', borderBottom: '1px solid rgba(233,233,237,0.09)' }}>
      <div className="k3-section-pad k3-grid-4" style={{ maxWidth: 1180, margin: '0 auto', padding: '44px 32px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 28 }}>
        {p.stats.map((s, i) => (
          <div key={i}>
            <div style={{ fontSize: 40, fontWeight: 500, letterSpacing: '-0.03em', lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: muted(0.72), marginTop: 10 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── 4. Products (intro + grid) ─────────────────────────────────────────────────
const product = z.object({
  name: z.string(),
  tag: z.string(),
  blurb: z.string(),
  points: z.array(z.string()).default([]),
  site: z.string(),
  href: z.string().default('#'),
});
export const k3ProductsSchema = z.object({
  kicker: z.string().default('Four products, one identity fabric'),
  heading: z.string().default("Every identity in your estate, governed by one company's stack."),
  intro: z.string().default('Each product ships and sells on its own. Together they cover the workforce, your customers, your privileged estate and the people who have to run it all.'),
  products: z.array(product).default([
    { name: 'Cymmetri', tag: 'Converged identity', site: 'cymmetri.com', href: 'https://cymmetri.com', blurb: 'One platform for employees, partners, customers and privileged users — lifecycle, governance and adaptive access in a single console.', points: ['Lifecycle automation', 'Governance', 'SSO + adaptive MFA', 'Passwordless'] },
    { name: 'Bastion', tag: 'Privileged & agent access', site: 'thebastion.io', href: 'https://thebastion.io', blurb: 'Privileged access that no longer stops at people. Humans, service accounts and AI agents each get a governed identity with a named owner.', points: ['Just-in-time access', 'Agent identity', 'LLM data protection', 'Break-glass'] },
    { name: 'IntelliAuth', tag: 'Customer authentication', site: 'intelliauth.io', href: 'https://intelliauth.io', blurb: 'Enterprise CIAM for product teams: build login, registration and MFA flows on a visual canvas, with multi-tenant isolation and data residency.', points: ['Visual flow builder', 'Multi-tenant', 'SAML / OIDC', 'Data residency'] },
    { name: 'Cymmetri Learn', tag: 'Enablement & LMS', site: 'learn.cymmetri.com', href: 'https://learn.cymmetri.com', blurb: 'The learning platform behind the stack — certification for admins and partners, plus security-awareness tracks for the workforce.', points: ['Admin certification', 'Partner enablement', 'Awareness tracks', 'Reporting'] },
  ]),
});
function K3Products(p: z.infer<typeof k3ProductsSchema>) {
  return (
    <>
      <section className="k3-section-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '88px 32px 24px' }}>
        <h6 style={{ color: ACCENT_LT, margin: '0 0 14px' }}>{p.kicker}</h6>
        <h2 style={{ fontSize: 38, letterSpacing: '-0.025em', maxWidth: '24ch', margin: '0 0 18px' }}>{p.heading}</h2>
        <p style={{ maxWidth: '60ch', color: muted(0.62), fontSize: 16 }}>{p.intro}</p>
      </section>
      <section className="k3-section-pad k3-grid-2" style={{ maxWidth: 1180, margin: '0 auto', padding: '24px 32px 88px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {p.products.map((pr, i) => (
          <div key={i} className="card" style={{ padding: 28, borderRadius: 14, background: SURFACE, boxShadow: `0 0 0 1px ${CARD_BORDER}`, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>{pr.name}</span>
              <span className="tag tag-outline" style={{ fontSize: 11 }}>{pr.tag}</span>
            </div>
            <p style={{ margin: 0, color: muted(0.68), fontSize: 15, lineHeight: 1.6, textWrap: 'pretty' }}>{pr.blurb}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 'auto', paddingTop: 8 }}>
              {pr.points.map((pt, j) => (
                <span key={j} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 999, background: 'rgba(145,132,217,0.12)', color: ACCENT_LT2 }}>{pt}</span>
              ))}
            </div>
            <a href={pr.href} target="_blank" rel="noreferrer" style={{ fontSize: 13, textDecoration: 'none', color: ACCENT_LT }}>{pr.site} →</a>
          </div>
        ))}
      </section>
    </>
  );
}

// ─── 5. Why-us pillars ───────────────────────────────────────────────────────
const pillar = z.object({ title: z.string(), body: z.string() });
export const k3PillarsSchema = z.object({
  kicker: z.string().default('Why enterprises pick us'),
  heading: z.string().default('Built in India, for regulated estates.'),
  pillars: z.array(pillar).default([
    { title: 'Indian product, global posture', body: "Built for heterogeneous IT-enabled estates, with data residency and local support that offshore vendors can't match." },
    { title: 'Converged, not stitched', body: 'Workforce, customer and privileged identity share one model — no three-vendor integration tax.' },
    { title: 'Agent-ready today', body: 'AI agents get real identities, scoped access and continuous risk scoring, in production now.' },
    { title: 'Deployed in weeks', body: 'Our engineers run the rollout. References in exchanges, banks and NBFCs will say so.' },
  ]),
});
function K3Pillars(p: z.infer<typeof k3PillarsSchema>) {
  return (
    <section style={{ borderTop: '1px solid rgba(233,233,237,0.09)', borderBottom: '1px solid rgba(233,233,237,0.09)', background: '#1a1c2c' }}>
      <div className="k3-section-pad k3-split" style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 32px', display: 'grid', gridTemplateColumns: '340px 1fr', gap: 56, alignItems: 'start' }}>
        <div>
          <h6 style={{ color: ACCENT_LT, margin: '0 0 14px' }}>{p.kicker}</h6>
          <h2 style={{ fontSize: 34, letterSpacing: '-0.025em', margin: 0 }}>{p.heading}</h2>
        </div>
        <div className="k3-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '34px 44px' }}>
          {p.pillars.map((w, i) => (
            <div key={i}>
              <div style={{ width: 20, height: 2, background: ACCENT, marginBottom: 14 }} />
              <h4 style={{ fontSize: 17, margin: '0 0 8px' }}>{w.title}</h4>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: muted(0.6) }}>{w.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 6. Trusted — logos + quotes + certs ────────────────────────────────────────
const quote = z.object({ text: z.string(), who: z.string(), org: z.string() });
export const k3TrustedSchema = z.object({
  kicker: z.string().default('Trusted in production'),
  logos: z.array(z.string()).default(['BSE Ltd.', 'Aditya Birla Finance', 'SBI Mutual Fund', 'Bank of India', 'Mahindra Finance']),
  quotes: z.array(quote).default([
    { text: 'With digitalization came the challenge of managing access to the application landscape — unified access and password fatigue were emerging as challenges. We implemented Cymmetri as our IAM solution.', who: 'Group CISO', org: 'BSE Ltd.' },
    { text: 'As we expanded into different finance verticals, Identity Management at a group level became an aspiration. Cymmetri is easy to implement and manage, and gave us a single-window approach for a vast number of employees.', who: 'Lead Info Security', org: 'Aditya Birla Finance Limited' },
  ]),
  certs: z.array(z.string()).default(['SOC 2', 'ISO 27001', 'Data residency (India)', 'GDPR-aligned']),
  certNote: z.string().default('Certification claims to be confirmed against current audit status before publishing.'),
});
function K3Trusted(p: z.infer<typeof k3TrustedSchema>) {
  return (
    <section className="k3-section-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '80px 32px' }}>
      <h6 style={{ color: muted(0.5), margin: '0 0 26px' }}>{p.kicker}</h6>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 44 }}>
        {p.logos.map((l, i) => (
          <span key={i} style={{ fontSize: 15, fontWeight: 500, padding: '12px 20px', borderRadius: 8, background: SURFACE, boxShadow: `0 0 0 1px ${CARD_BORDER}`, color: muted(0.82) }}>{l}</span>
        ))}
      </div>
      <div className="k3-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {p.quotes.map((q, i) => (
          <blockquote key={i} style={{ margin: 0, padding: 32, borderRadius: 14, background: SURFACE, boxShadow: `0 0 0 1px ${CARD_BORDER}` }}>
            <p style={{ margin: '0 0 20px', fontSize: 16, lineHeight: 1.7, color: muted(0.86), textWrap: 'pretty' }}>{q.text}</p>
            <footer style={{ fontSize: 13, color: muted(0.55) }}>
              <span style={{ color: ACCENT_LT2 }}>{q.who}</span>, {q.org}
            </footer>
          </blockquote>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 32 }}>
        {p.certs.map((c, i) => (
          <span key={i} className="tag tag-accent" style={{ fontSize: 12 }}>{c}</span>
        ))}
      </div>
      <p style={{ fontSize: 12, color: muted(0.4), marginTop: 16 }}>{p.certNote}</p>
    </section>
  );
}

// ─── 7. Closing CTA band ─────────────────────────────────────────────────────
export const k3CtaSchema = z.object({
  heading: z.string().default('Bring us your hardest access problem.'),
  body: z.string().default("Thirty minutes with our engineers — not a sales script. We'll tell you which of the four products you actually need."),
  primaryLabel: z.string().default('Talk to us'),
  primaryHref: z.string().default('/contact'),
  emailLabel: z.string().default('sales@cymmetri.com'),
  emailHref: z.string().default('mailto:sales@cymmetri.com'),
});
function K3Cta(p: z.infer<typeof k3CtaSchema>) {
  return (
    <section style={{ borderTop: '1px solid rgba(233,233,237,0.09)' }}>
      <div className="k3-section-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '84px 32px', display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 40, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 40, letterSpacing: '-0.03em', margin: '0 0 12px', maxWidth: '22ch' }}>{p.heading}</h2>
          <p style={{ margin: 0, color: muted(0.62), maxWidth: '52ch' }}>{p.body}</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <a className="btn btn-primary" href={p.primaryHref}>{p.primaryLabel}</a>
          <a className="btn btn-ghost" href={p.emailHref}>{p.emailLabel}</a>
        </div>
      </div>
    </section>
  );
}

// ─── 8. Footer ─────────────────────────────────────────────────────────────────
const footerLink = z.object({ label: z.string(), href: z.string().default('#') });
const footerCol = z.object({ title: z.string(), links: z.array(footerLink).default([]) });
export const k3FooterSchema = z.object({
  brand: z.string().default('Kathrefits AI'),
  blurb: z.string().default('Identity and access products for regulated enterprises and the AI agents inside them.'),
  columns: z.array(footerCol).default([
    { title: 'Products', links: [
      { label: 'Cymmetri', href: 'https://cymmetri.com' },
      { label: 'Bastion', href: 'https://thebastion.io' },
      { label: 'IntelliAuth', href: 'https://intelliauth.io' },
      { label: 'Cymmetri Learn', href: 'https://learn.cymmetri.com' },
    ] },
    { title: 'Company', links: [
      { label: 'About', href: '/about' },
      { label: 'Careers', href: '/contact' },
      { label: 'Partners', href: 'https://cymmetri.com/partner' },
      { label: 'Contact', href: '/contact' },
    ] },
    { title: 'Legal', links: [
      { label: 'Privacy policy', href: 'https://cymmetri.com/privacy-policy' },
      { label: 'Compliance', href: 'https://cymmetri.com/compliance' },
      { label: 'Terms', href: 'https://thebastion.io/terms' },
    ] },
  ]),
  copyright: z.string().default('© 2026 Kathrefits AI Pvt Ltd. Cymmetri, Bastion, IntelliAuth and Cymmetri Learn are our products.'),
});
function K3Footer(p: z.infer<typeof k3FooterSchema>) {
  return (
    <footer style={{ borderTop: '1px solid rgba(233,233,237,0.10)', background: '#131522' }}>
      <div className="k3-section-pad k3-grid-4" style={{ maxWidth: 1180, margin: '0 auto', padding: '56px 32px 28px', display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 40 }}>
        <div>
          <div style={{ marginBottom: 14 }}>
            <img src="/kathreftis/images/kathreftis-logo.png" alt={p.brand} style={{ height: 28, width: 'auto', display: 'block', background: '#fff', padding: '7px 13px', borderRadius: 12 }} />
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.7, color: muted(0.5), maxWidth: '34ch', margin: 0 }}>{p.blurb}</p>
        </div>
        {p.columns.map((col, i) => (
          <div key={i}>
            <h6 style={{ color: muted(0.5), margin: '0 0 12px' }}>{col.title}</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {col.links.map((lk, j) => (
                <a key={j} href={lk.href} style={{ fontSize: 13, color: muted(0.72), textDecoration: 'none' }}>{lk.label}</a>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="k3-section-pad" style={{ maxWidth: 1180, margin: '0 auto', padding: '0 32px 40px', fontSize: 12, color: muted(0.38) }}>{p.copyright}</div>
    </footer>
  );
}

// ─── Registry ──────────────────────────────────────────────────────────────────
export const kathreftisV3Blocks: BlockDefinition[] = [
  { type: 'k3Header', schema: k3HeaderSchema, Renderer: K3Header },
  { type: 'k3Hero', schema: k3HeroSchema, Renderer: K3Hero },
  { type: 'k3Stats', schema: k3StatsSchema, Renderer: K3Stats },
  { type: 'k3Products', schema: k3ProductsSchema, Renderer: K3Products },
  { type: 'k3Pillars', schema: k3PillarsSchema, Renderer: K3Pillars },
  { type: 'k3Trusted', schema: k3TrustedSchema, Renderer: K3Trusted },
  { type: 'k3Cta', schema: k3CtaSchema, Renderer: K3Cta },
  { type: 'k3Footer', schema: k3FooterSchema, Renderer: K3Footer },
];
